import { IconType } from "react-icons/lib";
import {
  MdAttachMoney,
  MdAssignment,
  MdDescription,
  MdEditDocument,
  MdGroups,
  MdInventory2,
  MdListAlt,
  MdPerson,
  MdReceiptLong,
  MdShoppingCart,
} from "react-icons/md";

import { DocType } from "@megaron/docs-contracts";

const uuidPattern = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";

const crmColor = "hsl(273, 75%, 25%)";
const invoicesColor = "hsl(234, 75%, 25%)";
const iamColor = "hsl(191, 75%, 25%)";
const supportColor = "hsl(200, 65%, 35%)";

const itemReservedSegments = new Set(["new", "sync"]);

const supportTicketReservedSegments = new Set(["nowe", "settings"]);

const matchGroup = (pattern: string) => {
  const regex = new RegExp(pattern);
  return (pathname: string): string | undefined => {
    const result = regex.exec(pathname);
    return result?.[1];
  };
};

export type HistoryDocRoute = {
  docType: DocType;
  label: string;
  appLabel: string;
  Icon: IconType;
  accentColor: string;
  matchIdentifier: (pathname: string) => string | undefined;
  buildPath: (identifier: string) => string;
};

export const historyDocRoutes: HistoryDocRoute[] = [
  {
    docType: "customer",
    label: "Klient",
    appLabel: "CRM",
    Icon: MdPerson,
    accentColor: crmColor,
    matchIdentifier: matchGroup(`^/crm/customers/id/(${uuidPattern})`),
    buildPath: (id) => `/crm/customers/id/${id}`,
  },
  {
    docType: "saved-view",
    label: "Lista CRM",
    appLabel: "CRM",
    Icon: MdListAlt,
    accentColor: crmColor,
    matchIdentifier: matchGroup(`^/crm/customers/(${uuidPattern})(?:/|$)`),
    buildPath: (id) => `/crm/customers/${id}`,
  },
  {
    docType: "order",
    label: "Zamówienie",
    appLabel: "CRM",
    Icon: MdShoppingCart,
    accentColor: crmColor,
    matchIdentifier: matchGroup(`^/crm/orders/([^/]+)(?:/|$)`),
    buildPath: (number) => `/crm/orders/${number}`,
  },
  {
    docType: "item",
    label: "Produkt",
    appLabel: "CRM",
    Icon: MdInventory2,
    accentColor: crmColor,
    matchIdentifier: (pathname) => {
      const identifier = matchGroup(`^/crm/items/([^/]+)(?:/|$)`)(pathname);
      if (!identifier || itemReservedSegments.has(identifier)) return undefined;
      return identifier;
    },
    buildPath: (id) => `/crm/items/${id}`,
  },
  {
    docType: "invoice-thread",
    label: "Faktura",
    appLabel: "Faktury",
    Icon: MdReceiptLong,
    accentColor: invoicesColor,
    matchIdentifier: matchGroup(`^/faktury/watki/(${uuidPattern})`),
    buildPath: (id) => `/faktury/watki/${id}`,
  },
  {
    docType: "invoice-draft",
    label: "Wersja robocza",
    appLabel: "Faktury",
    Icon: MdEditDocument,
    accentColor: invoicesColor,
    matchIdentifier: matchGroup(`^/faktury/wersje-robocze/(${uuidPattern})`),
    buildPath: (id) => `/faktury/wersje-robocze/${id}`,
  },
  {
    docType: "budget",
    label: "Budżet",
    appLabel: "Faktury",
    Icon: MdAttachMoney,
    accentColor: invoicesColor,
    matchIdentifier: matchGroup(`^/faktury/budzety/(${uuidPattern})`),
    buildPath: (id) => `/faktury/budzety/${id}`,
  },
  {
    docType: "user",
    label: "Użytkownik",
    appLabel: "IAM",
    Icon: MdPerson,
    accentColor: iamColor,
    matchIdentifier: matchGroup(`^/iam/accounts/email/([^/]+)(?:/|$)`),
    buildPath: (id) => `/iam/accounts/email/${id}`,
  },
  {
    docType: "user-group",
    label: "Grupa",
    appLabel: "IAM",
    Icon: MdGroups,
    accentColor: iamColor,
    matchIdentifier: matchGroup(`^/iam/groups/id/(${uuidPattern})`),
    buildPath: (id) => `/iam/groups/id/${id}`,
  },
  {
    docType: "support-ticket",
    label: "Zgłoszenie",
    appLabel: "Support",
    Icon: MdAssignment,
    accentColor: supportColor,
    matchIdentifier: (pathname) => {
      const identifier = matchGroup(`^/crm/zgloszenia/(${uuidPattern})(?:/|$)`)(pathname);
      if (!identifier || supportTicketReservedSegments.has(identifier)) return undefined;
      return identifier;
    },
    buildPath: (id) => `/crm/zgloszenia/${id}`,
  },
];

export const fallbackDocIcon: IconType = MdDescription;

export const fallbackAccentColor = crmColor;

export const historyDocRouteByType = new Map(historyDocRoutes.map((route) => [route.docType, route]));
